/**
 * 页面头部组件
 * 包含面包屑、页面标题、操作按钮等
 */

import React from 'react';
import { Breadcrumb, Button, Space, Tabs } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import type { BreadcrumbProps, TabsProps } from 'antd';
import './PageHeader.css';

export interface PageHeaderAction {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  type?: 'default' | 'primary' | 'dashed' | 'link' | 'text';
  danger?: boolean;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  dropdown?: {
    items: any[];
  };
}

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  description?: string;
  icon?: React.ReactNode;
  breadcrumb?: Array<{ label: string; path?: string }>;
  breadcrumbs?: BreadcrumbProps['items'];
  actions?: PageHeaderAction[];
  tabs?: TabsProps['items'];
  activeTabKey?: string;
  onTabChange?: (key: string) => void;
  onBack?: () => void;
  extra?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  description,
  icon,
  breadcrumb,
  breadcrumbs,
  actions,
  tabs,
  activeTabKey,
  onTabChange,
  extra,
}) => {
  // 默认面包屑（首页）
  const defaultBreadcrumbs: BreadcrumbProps['items'] = [
    {
      href: '/',
      title: (
        <>
          <HomeOutlined />
          <span>首页</span>
        </>
      ),
    },
  ];

  // 处理breadcrumb prop（旧格式）
  let finalBreadcrumbs = breadcrumbs || defaultBreadcrumbs;
  if (breadcrumb && breadcrumb.length > 0) {
    finalBreadcrumbs = breadcrumb.map(item => ({
      title: item.label,
      href: item.path,
    }));
  }

  return (
    <div className="app-page-header">
      {/* 面包屑导航 */}
      {finalBreadcrumbs && finalBreadcrumbs.length > 0 && (
        <div className="app-page-header-breadcrumb">
          <Breadcrumb items={finalBreadcrumbs} />
        </div>
      )}

      {/* 主要内容区 */}
      <div className="app-page-header-main">
        {/* 标题区域 - 左对齐 */}
        <div className="app-page-header-title-section">
          <div className="app-page-header-title-wrapper">
            {icon && <span className="app-page-header-icon">{icon}</span>}
            <h1 className="app-page-header-title">{title}</h1>
          </div>
          {(subtitle || description) && (
            <p className="app-page-header-subtitle">{subtitle || description}</p>
          )}
        </div>

        {/* 操作区域 - 右对齐 */}
        {(actions || extra) && (
          <div className="app-page-header-actions">
            {actions && actions.length > 0 && (
              <Space size="middle">
                {actions.map((action) => (
                  <Button
                    key={action.key}
                    type={action.type || 'default'}
                    icon={action.icon}
                    onClick={action.onClick}
                    loading={action.loading}
                    disabled={action.disabled}
                    style={action.style}
                  >
                    {action.label}
                  </Button>
                ))}
              </Space>
            )}
            {extra}
          </div>
        )}
      </div>

      {/* 标签页导航 */}
      {tabs && tabs.length > 0 && (
        <div className="app-page-header-tabs">
          <Tabs
            items={tabs}
            activeKey={activeTabKey}
            onChange={onTabChange}
            size="large"
          />
        </div>
      )}
    </div>
  );
};

export default PageHeader;
